import { useEffect } from 'react'
import { useSelector } from 'react-redux'
import styled from 'styled-components'
import { Container, hooks, Image as RuiImage, Loader, Spacer, Text, Button } from 'roadie-ui'
import { useDispatch } from 'hooks/useDispatch'
import {
  Container as Framework,
  QuizModule,
  RequiredQuiz,
  Slide as SlideBase,
} from 'components/pages/certifications/framework'
import Oops from 'components/pages/certifications/Oops'
import { fetchDriverCerts } from 'actions/profile'
import { TRAINING_CERTS } from 'util/constants'
import slide01Intro from 'images/returns/01-intro.png'
import slide02BackToStore from 'images/returns/02-back_to_store.png'
import slide02LabelForReturn from 'images/returns/02-label_for_return.png'
import slide02PickupHouse from 'images/returns/02-pick_up_house.png'
import slide03Step1 from 'images/returns/03-step1.png'
import slide03Step2 from 'images/returns/03-step2.gif'
import slide04Step3 from 'images/returns/04-step3.png'
import slide04Step4 from 'images/returns/04-step4.gif'
import slide05Step5 from 'images/returns/05-step5.gif'
import slide05Step6 from 'images/returns/05-step6.gif'
import slide06Step7_1 from 'images/returns/06-step7_01.png'
import slide06Step7_2 from 'images/returns/06-step7_02.gif'
import slide06Step8 from 'images/returns/06-step8.gif'
import slide08Step11 from 'images/returns/08-step11.png'
import slide09Image from 'images/returns/09-image.png'
import bannerPass from 'images/returns/banner-pass.png'
import bannerFail from 'images/returns/banner-fail.png'
import { LocalizedRichText, useLocalization, LocalizedText } from 'localization'
const ReturnsPage: React.FC = () => {
  const { t } = useLocalization()
  const loading = hooks.useToggle(true)
  const dispatch = useDispatch()
  const trainingCertifications = useSelector<
    any,
    {
      name: string
    }[]
  >((state) => state.trainingCertifications?.delivery_certifications_drivers ?? [])
  useEffect(() => {
    if (loading.value) {
      fetchDriverCerts()(dispatch).then(loading.off)
    }
  }, [dispatch, loading])
  const driverHasCert = trainingCertifications.find((cert) => cert.name === TRAINING_CERTS.RETURNS.ELIGIBLE_CERT)
  if (loading.value) {
    return (
      <RoadieReturns>
        <Loader />
      </RoadieReturns>
    )
  }
  if (!driverHasCert) {
    return (
      <RoadieReturns>
        <Oops
          message={t('dweb_certifications_returns_not_eligible_message')}
          url={`${import.meta.env.REACT_APP_SUPPORT_URL}/support/s/article/Roadie-Returns-FAQs`}
          urlTextKey="dweb_certifications_returns_learn_more_about_returns"
        />
      </RoadieReturns>
    )
  }
  return (
    <RoadieReturns>
      <QuizModule certification={TRAINING_CERTS.RETURNS.CERT} minimumScoreToPass={5}>
        {{
          slides: (
            <>
              <Slide>
                <Image src={slide01Intro} />
                <Spacer $height="3.2rem" />
                <Text.Overline $color="primary-base">
                  <LocalizedText translationKey="dweb_certifications_returns_returns_start_delivering_roadie_returns" />
                </Text.Overline>
                <Spacer $height="0.4rem" />
                <Text.H4>
                  <LocalizedText translationKey="dweb_certifications_returns_returns_welcome_roadie_returns" />
                </Text.H4>
                <Spacer $height="0.8rem" />
                <Text.Body2>
                  <LocalizedText translationKey="dweb_certifications_returns_attention_intro" />
                  <br />
                  <br />
                  <LocalizedText translationKey="dweb_certifications_returns_guide_intro" />
                </Text.Body2>
              </Slide>
              <Slide>
                <Title>
                  <LocalizedText translationKey="dweb_certifications_returns_returns_what_roadie_returns" />
                </Title>
                <Spacer $height="3.2rem" />
                <Text.Body2>
                  <LocalizedText translationKey="dweb_certifications_returns_returns_roadie_returns_new_type_multi_stop_gig_dedicated" />
                  <br />
                  <br />
                  <LocalizedText translationKey="dweb_certifications_returns_returns_every_roadie_returns_gig_will_start_end_at" />
                </Text.Body2>
                <Spacer $height="3.2rem" />
                <QAItem image={slide02LabelForReturn}>
                  <LocalizedRichText
                    translationKey="dweb_certifications_returns_returns_one_stop_strong_text1_from_nearby_ups_store"
                    params={{
                      strongText1: (
                        <strong>
                          <LocalizedText translationKey="dweb_certifications_returns_returns_pick_up_return_labels" />
                        </strong>
                      ),
                    }}
                  />
                </QAItem>
                <Spacer $height="3.2rem" />
                <QAItem image={slide02PickupHouse}>
                  <LocalizedRichText
                    translationKey="dweb_certifications_returns_stops_to_collect_from_customers"
                    params={{
                      strongText1: (
                        <strong>
                          <LocalizedText translationKey="dweb_certifications_returns_returns_pick_up_packages_return" />
                        </strong>
                      ),
                    }}
                  />
                </QAItem>
                <Spacer $height="3.2rem" />
                <QAItem image={slide02BackToStore}>
                  <LocalizedRichText
                    translationKey="dweb_certifications_returns_returns_one_stop_strong_text1_nearby_ups_store_location"
                    params={{
                      strongText1: (
                        <strong>
                          <LocalizedText translationKey="dweb_certifications_returns_returns_drop_off_all_collected_packages" />
                        </strong>
                      ),
                    }}
                  />
                </QAItem>
              </Slide>
              <Slide>
                <Title>
                  <LocalizedText translationKey="dweb_certifications_returns_returns_picking_up_labels_at_ups_store" />
                </Title>
                <Spacer $height="3.2rem" />
                <Step number={1} image={slide03Step1}>
                  <Text.Body2>
                    <LocalizedText translationKey="dweb_certifications_returns_returns_first_stop_route_will_at_ups_store_print" />
                    <br />
                    <br />
                    <LocalizedText translationKey="dweb_certifications_returns_returns_if_kiosk_out_order_will_not_print_labels" />
                  </Text.Body2>
                </Step>
                <Spacer $height="3.2rem" />
                <Step number={2} image={slide03Step2}>
                  <Text.Body2>
                    <LocalizedText translationKey="dweb_certifications_returns_returns_tap_green_arrow_app_reveal_qr_code_s" />
                    <br />
                    <br />
                    <LocalizedText translationKey="dweb_certifications_returns_returns_open_first_qr_code_scan_it_at_kiosk" />
                  </Text.Body2>
                </Step>
              </Slide>
              <Slide>
                <Title>
                  <LocalizedText translationKey="dweb_certifications_returns_returns_picking_up_labels_at_ups_store" />
                </Title>
                <Spacer $height="3.2rem" />
                <Step number={3} image={slide04Step3}>
                  <Text.Body2>
                    <LocalizedText translationKey="dweb_certifications_returns_collect_labels_instruction" />
                    <br />
                    <br />
                    <LocalizedText translationKey="dweb_certifications_returns_returns_if_label_reads_do_not_attempt_pickup_click" />
                  </Text.Body2>
                </Step>
                <Spacer $height="3.2rem" />
                <Step number={4} image={slide04Step4}>
                  <Text.Body2>
                    <LocalizedText translationKey="dweb_certifications_returns_scan_printed_labels" />
                    <br />
                    <br />
                    <LocalizedText translationKey="dweb_certifications_returns_reprint_missing_labels" />
                    <br />
                    <br />
                    <LocalizedText translationKey="dweb_certifications_returns_returns_open_first_qr_code_scan_it_at_kiosk" />
                  </Text.Body2>
                </Step>
              </Slide>
              <Slide>
                <Title>
                  <LocalizedText translationKey="dweb_certifications_returns_returns_picking_up_return_orders" />
                </Title>
                <Spacer $height="3.2rem" />
                <Step number={5} image={slide05Step5}>
                  <Text.Body2>
                    <LocalizedRichText
                      translationKey="dweb_certifications_returns_navigation_instruction"
                      params={{
                        br: <br />,
                        strongText1: (
                          <strong>
                            <LocalizedText translationKey="dweb_certifications_returns_returns_cant_access_package_pick_up_location" />
                          </strong>
                        ),
                      }}
                    />
                  </Text.Body2>
                </Step>
                <Spacer $height="3.2rem" />
                <Step number={6} image={slide05Step6}>
                  <Text.Body2>
                    <LocalizedText translationKey="dweb_certifications_returns_returns_when_arrive_look_at_order_number_s_displayed" />
                    <br />
                    <br />
                    <LocalizedText translationKey="dweb_certifications_returns_returns_scan_each_label_app_make_sure_youve_got" />
                  </Text.Body2>
                </Step>
              </Slide>
              <Slide>
                <Title>
                  <LocalizedText translationKey="dweb_certifications_returns_returns_picking_up_return_orders" />
                </Title>
                <Spacer $height="3.2rem" />
                <Step number={7} image={slide06Step7_1}>
                  <Text.Body2>
                    <LocalizedRichText
                      translationKey="dweb_certifications_returns_returns_after_scanning_label_s_head_front_door_business"
                      params={{
                        strongText1: (
                          <strong>
                            <LocalizedText translationKey="dweb_certifications_returns_returns_completely" />
                          </strong>
                        ),
                      }}
                    />
                  </Text.Body2>
                </Step>
                <Step image={slide06Step7_2}>
                  <Text.Body2>
                    <LocalizedText translationKey="dweb_certifications_returns_collect_packages_instruction" />
                    <br />
                    <br />
                    <LocalizedText translationKey="dweb_certifications_returns_returns_if_there_multiple_packages_pick_up_at_single" />
                  </Text.Body2>
                </Step>
                <Spacer $height="3.2rem" />
                <Step number={8} image={slide06Step8}>
                  <Text.Body2>
                    <LocalizedText translationKey="dweb_certifications_returns_pickup_conditions_confirmation" />
                    <br />
                    <br />
                    <LocalizedText translationKey="dweb_certifications_returns_complete_pickup_instruction" />
                    <br />
                    <br />
                    <LocalizedText translationKey="dweb_certifications_returns_returns_repeat_pick_up_process_at_each_location_route" />
                  </Text.Body2>
                </Step>
              </Slide>
              <Slide>
                <Title>
                  <LocalizedText translationKey="dweb_certifications_returns_returns_troubleshooting_issues_at_pickup" />
                </Title>
                <Spacer $height="3.2rem" />
                <Text.Body2>
                  <LocalizedText translationKey="dweb_returns_common_pickup_issues_intro" />
                  <ul>
                    <li>
                      <LocalizedText translationKey="dweb_certifications_returns_returns_no_package_available_pick_up" />
                    </li>
                    <li>
                      <LocalizedText translationKey="dweb_certifications_returns_package_count_mismatch" />
                    </li>
                    <li>
                      <LocalizedText translationKey="dweb_certifications_returns_returns_not_sure_what_pick_up" />
                    </li>
                    <li>
                      <LocalizedText translationKey="dweb_certifications_returns_returns_package_damaged_torn_leaking_etc" />
                    </li>
                    <li>
                      <LocalizedText translationKey="dweb_certifications_returns_returns_item_s_not_boxed_package_not_taped_sealed" />
                    </li>
                    <li>
                      <LocalizedText translationKey="dweb_certifications_returns_returns_customer_cant_doesnt_want_provide_package_s" />
                    </li>
                    <li>
                      <LocalizedText translationKey="dweb_certifications_returns_returns_customer_already_completed_return" />
                    </li>
                  </ul>
                  <LocalizedText translationKey="dweb_returns_knock_on_door_pickup_issue" />
                  <br />
                  <br />
                  <LocalizedText translationKey="dweb_returns_unresolved_pickup_issue" />
                  <br />
                  <br />
                  <strong>
                    <LocalizedText translationKey="dweb_certifications_returns_returns_if_customer_at_pickup_location" />
                  </strong>
                  <br />
                  <LocalizedText translationKey="dweb_returns_leave_labels_with_customer" />
                </Text.Body2>
                <Spacer $height="1.6rem" />
                <Video src="07-video1.mp4" />
                <Spacer $height="3.2rem" />
                <Text.Body2>
                  <LocalizedRichText
                    translationKey="dweb_certifications_returns_returns_leave_label_at_pickup_location"
                    params={{
                      strongText1: (
                        <strong>
                          <LocalizedText translationKey="dweb_certifications_returns_returns_if_customer_not_at_pickup_location" />
                        </strong>
                      ),
                      br: <br />,
                    }}
                  />
                </Text.Body2>
                <Spacer $height="1.6rem" />
                <Video src="07-video2.mp4" />
              </Slide>
              <Slide>
                <Title>
                  <LocalizedText translationKey="dweb_certifications_returns_returns_package_dropoff_at_ups_store" />
                </Title>
                <Spacer $height="3.2rem" />
                <Step number={9} video="08-step9.mp4">
                  <Text.Body2>
                    <LocalizedText translationKey="dweb_certifications_returns_returns_final_stop_drop_off_all_return_packages_at" />
                    <br />
                    <br />
                    <LocalizedText translationKey="dweb_certifications_returns_returns_if_kiosk_out_order_will_not_print_labels" />
                  </Text.Body2>
                </Step>
                <Spacer $height="3.2rem" />
                <Step number={10} video="08-step10.mp4">
                  <Text.Body2>
                    <LocalizedText translationKey="dweb_certifications_returns_returns_after_scanning_label_at_kiosk_click_my_item" />
                    <br />
                    <br />
                    <LocalizedText translationKey="dweb_certifications_returns_returns_click_app_scan_new_shipping_label_phone" />
                  </Text.Body2>
                </Step>
                <Spacer $height="3.2rem" />
                <Step number={11} image={slide08Step11}>
                  <Text.Body2>
                    <LocalizedText translationKey="dweb_certifications_returns_returns_drop_package_return_bin_located_by_kiosk_if" />
                    <br />
                    <br />
                    <LocalizedText translationKey="dweb_certifications_returns_repeat_dropoff_until_complete" />
                  </Text.Body2>
                </Step>
              </Slide>
              <Slide>
                <Image src={slide09Image} />
                <Spacer $height="3.2rem" />
                <Title>
                  <LocalizedText translationKey="dweb_certifications_pharmacy_pharmacy_show_what_know" />
                </Title>
                <Spacer $height="1.6rem" />
                <Text.Body2>
                  <LocalizedText translationKey="dweb_certifications_returns_quiz_intro" />
                  <br />
                  <br />
                  <LocalizedText translationKey="dweb_certifications_returns_returns_good_luck" />
                </Text.Body2>
              </Slide>
            </>
          ),
          questions: (
            <>
              <Slide>
                <RequiredQuiz
                  questionNumber={1}
                  totalQuestions={6}
                  questionKey="dweb_certifications_returns_quiz_q1_question"
                  descriptionKey="dweb_certifications_returns_quiz_q1_description"
                  options={[
                    {
                      textKey: 'dweb_certifications_returns_quiz_q1_option_1',
                      value: '1',
                    },
                    {
                      textKey: 'dweb_certifications_returns_quiz_q1_option_2',
                      value: '2',
                      isCorrect: true,
                    },
                    {
                      textKey: 'dweb_certifications_returns_quiz_q1_option_3',
                      value: '3',
                    },
                    {
                      textKey: 'dweb_certifications_returns_quiz_q1_option_4',
                      value: '4',
                    },
                  ]}
                />
              </Slide>
              <Slide>
                <RequiredQuiz
                  questionNumber={2}
                  totalQuestions={6}
                  questionKey="dweb_certifications_returns_quiz_q2_question"
                  descriptionKey="dweb_certifications_returns_quiz_q2_description"
                  options={[
                    {
                      textKey: 'dweb_certifications_returns_quiz_q2_option_1',
                      value: '1',
                    },
                    {
                      textKey: 'dweb_certifications_returns_quiz_q2_option_2',
                      value: '2',
                    },
                    {
                      textKey: 'dweb_certifications_returns_quiz_q2_option_3',
                      value: '3',
                      isCorrect: true,
                    },
                    {
                      textKey: 'dweb_certifications_returns_quiz_q2_option_4',
                      value: '4',
                    },
                  ]}
                />
              </Slide>
              <Slide>
                <RequiredQuiz
                  questionNumber={3}
                  totalQuestions={6}
                  questionKey="dweb_certifications_returns_quiz_q3_question"
                  descriptionKey="dweb_certifications_returns_quiz_q3_description"
                  options={[
                    {
                      textKey: 'dweb_certifications_returns_quiz_q3_option_1',
                      value: '1',
                      isCorrect: true,
                    },
                    {
                      textKey: 'dweb_certifications_returns_quiz_q3_option_2',
                      value: '2',
                    },
                    {
                      textKey: 'dweb_certifications_returns_quiz_q3_option_3',
                      value: '3',
                    },
                    {
                      textKey: 'dweb_certifications_returns_quiz_q3_option_4',
                      value: '4',
                    },
                  ]}
                />
              </Slide>
              <Slide>
                <RequiredQuiz
                  questionNumber={4}
                  totalQuestions={6}
                  questionKey="dweb_certifications_returns_quiz_q4_question"
                  descriptionKey="dweb_certifications_returns_quiz_q4_description"
                  options={[
                    {
                      textKey: 'dweb_certifications_returns_quiz_q4_option_1',
                      value: '1',
                    },
                    {
                      textKey: 'dweb_certifications_returns_quiz_q4_option_2',
                      value: '2',
                    },
                    {
                      textKey: 'dweb_certifications_returns_quiz_q4_option_3',
                      value: '3',
                      isCorrect: true,
                    },
                    {
                      textKey: 'dweb_certifications_returns_quiz_q4_option_4',
                      value: '4',
                    },
                  ]}
                />
              </Slide>
              <Slide>
                <RequiredQuiz
                  questionNumber={5}
                  totalQuestions={6}
                  questionKey="dweb_certifications_returns_quiz_q5_question"
                  descriptionKey="dweb_certifications_returns_quiz_q5_description"
                  options={[
                    {
                      textKey: 'dweb_certifications_returns_quiz_q5_option_1',
                      value: '1',
                    },
                    {
                      textKey: 'dweb_certifications_returns_quiz_q5_option_2',
                      value: '2',
                      isCorrect: true,
                    },
                    {
                      textKey: 'dweb_certifications_returns_quiz_q5_option_3',
                      value: '3',
                    },
                    {
                      textKey: 'dweb_certifications_returns_quiz_q5_option_4',
                      value: '4',
                    },
                  ]}
                />
              </Slide>
              <Slide>
                <RequiredQuiz
                  questionNumber={6}
                  totalQuestions={6}
                  questionKey="dweb_certifications_returns_quiz_q6_question"
                  descriptionKey="dweb_certifications_returns_quiz_q6_description"
                  options={[
                    {
                      textKey: 'dweb_certifications_returns_quiz_q6_option_1',
                      value: '1',
                      isCorrect: true,
                    },
                    {
                      textKey: 'dweb_certifications_returns_quiz_q6_option_2',
                      value: '2',
                    },
                    {
                      textKey: 'dweb_certifications_returns_quiz_q6_option_3',
                      value: '3',
                    },
                    {
                      textKey: 'dweb_certifications_returns_quiz_q6_option_4',
                      value: '4',
                    },
                    {
                      textKey: 'dweb_certifications_returns_quiz_q6_option_5',
                      value: '5',
                    },
                  ]}
                />
              </Slide>
            </>
          ),
          resultSlide: (results: { passed: boolean; numCorrectAnswers: number; resetModule: VoidFunction }) =>
            results.passed ? (
              <Slide>
                <Image src={bannerPass} />
                <Spacer $height="3.2rem" />
                <Text.H4>
                  <LocalizedText translationKey="dweb_certifications_mail_innovations_great_job" />
                </Text.H4>
                <Spacer $height="1.6rem" />
                <Text.Body2 $fontWeight="bold">
                  <LocalizedRichText
                    translationKey="dweb_certifications_returns_returns_got_value1_out_6_questions_correct"
                    params={{
                      value1: results.numCorrectAnswers,
                    }}
                  />
                </Text.Body2>
                <Spacer $height="1.6rem" />
                <Text.Body2>
                  <LocalizedText translationKey="dweb_certifications_returns_completion_message" />
                  <br />
                  <br />
                  <LocalizedText translationKey="dweb_certifications_returns_returns_now_youre_ready_roadie_returns_gigs_check_out" />
                </Text.Body2>
              </Slide>
            ) : (
              <Slide>
                <Image src={bannerFail} />
                <Spacer $height="3.2rem" />
                <Text.H4>
                  <LocalizedText translationKey="dweb_certifications_returns_returns_lets_try_again" />
                </Text.H4>
                <Spacer $height="1.6rem" />
                <Text.Body2 $fontWeight="bold">
                  <LocalizedRichText
                    translationKey="dweb_certifications_returns_returns_only_got_value1_out_6_correct"
                    params={{
                      value1: results.numCorrectAnswers,
                    }}
                  />
                </Text.Body2>
                <Spacer $height="1.6rem" />
                <Text.Body2>
                  <LocalizedText translationKey="dweb_certifications_returns_returns_while_we_appreciate_effort_it_looks_like_didnt" />
                  <br />
                  <br />
                  <LocalizedText translationKey="dweb_certifications_returns_returns_good_luck" />
                </Text.Body2>
                <Spacer $height="3.2rem" />
                <Button.Primary $width="100%" onClick={results.resetModule}>
                  <LocalizedText translationKey="dweb_certifications_pharmacy_pharmacy_start_over" />
                </Button.Primary>
              </Slide>
            ),
        }}
      </QuizModule>
    </RoadieReturns>
  )
}
const QAItem: React.FC<
  React.PropsWithChildren<{
    image: string
  }>
> = (props) => (
  <Container.Grid $gridTemplateColumns="7.2rem 1fr">
    <Image src={props.image} />
    <Container.Base $padding="0.8rem 1.6rem">
      <Text.Body2>{props.children}</Text.Body2>
    </Container.Base>
  </Container.Grid>
)
const Slide: React.FC<React.PropsWithChildren> = (props) => (
  <SlideBase>
    {props.children}
    <Spacer $height="3.2rem" />
  </SlideBase>
)
const Image: React.FC<{
  src: string
}> = (props) => <RuiImage $width="100%" src={props.src} />
const Video: React.FC<{
  src: string
}> = (props) => (
  <StyledVideo src={`${import.meta.env.BASE_URL}Returns/${props.src}`} controls={false} loop autoPlay playsInline />
)
const StyledVideo = styled.video`
  width: 100%;
`
const Title: React.FC<React.PropsWithChildren> = (props) => <Text.H5 $fontWeight="bold" {...props} />
const Step: React.FC<
  React.PropsWithChildren<
    | {
        number?: number
        image: string
      }
    | {
        number?: number
        video: string
      }
  >
> = (props) => (
  <>
    {props.number ? (
      <Text.Subtitle1 $fontWeight="500">
        <LocalizedRichText
          translationKey="dweb_certifications_returns_returns_step_value1"
          params={{
            value1: props.number,
          }}
        />
      </Text.Subtitle1>
    ) : null}
    <Spacer $height="1.6rem" />
    {'image' in props ? <Image src={props.image} /> : <Video src={props.video} />}
    <Spacer $height="1.6rem" />
    {props.children}
  </>
)
const RoadieReturns: React.FC<React.PropsWithChildren> = (props) => {
  const { t } = useLocalization()

  return (
    <Container.Base $color="neutral-black">
      <Framework title={t('dweb_certifications_returns_returns_roadie_returns')} {...props} />
    </Container.Base>
  )
}
export default ReturnsPage
